import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { SpinnerIcon, ChevronLeftIcon } from '@/components/ui/icons'
import { useFirebaseAuth } from '@/features/Authentication/hooks/useFirebaseAuth'
import {
  signInWithMobileNumber,
  verifyMobileOTP,
  sendMobileOTP,
  verifyOTP,
  resendOTP,
} from '@/features/Authentication/api'
import { config } from '@/config/config'

type AuthStep = 'method' | 'phone-input' | 'otp' | 'user-details'

interface PhoneFormData {
  mobileNumber: string
  countryCode: string
}

interface UserDetailsFormData {
  firstName: string
  lastName: string
  email: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    signInWithGoogle,
    signInWithApple,
    checkMobileUser,
    completeMobileAuth,
    loading: authLoading,
    isOAuthAvailable,
    stage: authStage,
    appUser,
    idToken,
  } = useFirebaseAuth()

  const returnTo = (location.state as { returnTo?: string })?.returnTo || '/'

  const [step, setStep] = useState<AuthStep>('method')
  const [phone, setPhone] = useState<PhoneFormData>({ mobileNumber: '', countryCode: '+61' })
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [userDetails, setUserDetails] = useState<UserDetailsFormData>({ firstName: '', lastName: '', email: '' })
  const [isNewUser, setIsNewUser] = useState(false)
  const [isOAuthNewUser, setIsOAuthNewUser] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)

  const otpRefs = useRef<(HTMLInputElement | null)[]>([])
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const fullMobileNumber = `${phone.countryCode}${phone.mobileNumber}`
  const restaurantId = config.store.restaurantId
  const storeId = config.store.storeId

  const [redirectPending] = useState(
    () => sessionStorage.getItem('oauth_redirect_pending') === 'true'
  )

  const isBusy = loading || authLoading

  // Debug info (can be removed after testing)
  const debugInfo = {
    step,
    authStage,
    authLoading,
    loading,
    redirectPending,
    isOAuthNewUser,
    isNewUser,
    hasAppUser: !!appUser,
    hasIdToken: !!idToken,
    error,
  }

  // ─── Effects ───────────────────────────────────────────────

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault()
      handleBack()
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [step])

  useEffect(() => {
    if (step !== 'method') {
      window.history.pushState({ step }, '')
    }
  }, [step])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  useEffect(() => {
    const handleResize = () => {
      if (document.activeElement?.tagName === 'INPUT') {
        setTimeout(() => {
          document.activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      }
    }
    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', handleResize)
      return () => vv.removeEventListener('resize', handleResize)
    }
  }, [])

  // ─── Handle OAuth Redirect Result ──────────────────────────

  useEffect(() => {
    if (!redirectPending) return

    console.log('[LoginPage] Redirect pending, auth stage:', authStage, 'loading:', authLoading)

    // Timeout to prevent infinite loading if redirect fails
    const timeout = setTimeout(() => {
      console.log('[LoginPage] Timeout reached, auth stage:', authStage)
      if (authStage === 'idle' && !authLoading) {
        sessionStorage.removeItem('oauth_redirect_pending')
        setError('Sign-in redirect failed. This domain may not be configured for redirect authentication. The page will reload to try popup instead.')
        // Reload after showing error briefly
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      }
    }, 10000) // 10 second timeout

    if (authStage === 'oauth_needs_phone') {
      console.log('[LoginPage] OAuth needs phone, showing phone input')
      clearTimeout(timeout)
      sessionStorage.removeItem('oauth_redirect_pending')
      setIsOAuthNewUser(true)
      setStep('phone-input')
    } else if (authStage === 'authenticated') {
      console.log('[LoginPage] Authenticated, navigating to:', returnTo)
      clearTimeout(timeout)
      sessionStorage.removeItem('oauth_redirect_pending')
      navigate(returnTo)
    }

    return () => clearTimeout(timeout)
  }, [authStage, redirectPending, navigate, returnTo, authLoading])

  // ─── Navigation ────────────────────────────────────────────

  const handleBack = useCallback(() => {
    setError(null)
    switch (step) {
      case 'method':
        navigate(returnTo)
        break
      case 'phone-input':
        setIsOAuthNewUser(false)
        setStep('method')
        break
      case 'otp':
        setStep(isNewUser && !isOAuthNewUser ? 'user-details' : 'phone-input')
        setOtp(['', '', '', '', '', ''])
        break
      case 'user-details':
        setStep('phone-input')
        break
    }
  }, [step, navigate, returnTo])

  // ─── OAuth Handlers ────────────────────────────────────────

  const handleGoogleLogin = async () => {
    setError(null)
    try {
      const result = await signInWithGoogle()
      if (!result) return
      if (result.isNewUser) {
        setIsOAuthNewUser(true)
        setStep('phone-input')
      } else {
        navigate(returnTo)
      }
    } catch {
      setError('Google sign-in failed. Please try again.')
    }
  }

  const handleAppleLogin = async () => {
    setError(null)
    try {
      const result = await signInWithApple()
      if (!result) return
      if (result.isNewUser) {
        setIsOAuthNewUser(true)
        setStep('phone-input')
      } else {
        navigate(returnTo)
      }
    } catch {
      setError('Apple sign-in failed. Please try again.')
    }
  }

  // ─── Phone Flow Handlers ───────────────────────────────────

  const handlePhoneSubmit = async () => {
    if (phone.mobileNumber.length < 8) {
      setError('Please enter a valid phone number')
      return
    }

    setLoading(true)
    setError(null)

    try {
      if (isOAuthNewUser) {
        const otpResult = await sendMobileOTP({
          mobileNumber: fullMobileNumber,
          restaurantId,
          storeId,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })

        if (otpResult?.errors?.length > 0 || !otpResult?.data?.isOtpSent) {
          setError(otpResult?.errors?.[0] || 'Failed to send OTP. Please try again.')
          return
        }
      } else {
        const { userExists, user } = await checkMobileUser(fullMobileNumber)
        setIsNewUser(!userExists)

        if (!userExists) {
          setStep('user-details')
          return
        }

        const signInResult = await signInWithMobileNumber({
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          mobileNumber: fullMobileNumber,
          email: user?.email ?? '',
          restaurantId,
          storeId,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })

        if (signInResult?.errors?.length > 0) {
          setError(signInResult.errors[0] || 'Server error, please try again')
          return
        }
      }

      setStep('otp')
      setResendCooldown(30)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
    if (e.key === 'Enter' && otp.every((d) => d !== '')) {
      handleOtpSubmit()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const newOtp = [...otp]
    for (let i = 0; i < pasted.length; i++) {
      newOtp[i] = pasted[i]
    }
    setOtp(newOtp)
    const nextEmpty = newOtp.findIndex((d) => !d)
    otpRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus()
  }

  const handleOtpSubmit = async () => {
    const otpString = otp.join('')
    if (otpString.length !== 6) {
      setError('Please enter the 6-digit code')
      return
    }

    setLoading(true)
    setError(null)

    try {
      if (isOAuthNewUser) {
        const result = await verifyOTP(
          {
            mobileNumber: fullMobileNumber,
            otp: otpString,
            restaurantId,
            storeId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          idToken || ''
        )

        const {
          successful = false,
          errors = [],
          token,
          user,
          linkProvider,
        } = result?.data ?? {}

        if (!successful) {
          setError(errors?.[0] || 'Incorrect OTP entered')
          return
        }

        if (user && token) {
          await completeMobileAuth(user, token, linkProvider)
          navigate(returnTo)
        }
      } else {
        const result = await verifyMobileOTP({
          mobileNumber: fullMobileNumber,
          otp: otpString,
          restaurantId,
          storeId,
        })

        const {
          successful = false,
          errors = [],
          token,
          user,
        } = result?.data ?? {}

        if (!successful) {
          setError(errors?.[0] || 'Incorrect OTP entered')
          return
        }

        if (user && token) {
          await completeMobileAuth(user, token)
        }

        navigate(returnTo)
      }
    } catch (err: any) {
      setError(err?.message || 'Invalid OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return

    setLoading(true)
    setError(null)

    try {
      if (isOAuthNewUser) {
        const otpResult = await sendMobileOTP({
          mobileNumber: fullMobileNumber,
          restaurantId,
          storeId,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })

        if (otpResult?.errors?.length > 0 || !otpResult?.data?.isOtpSent) {
          setError(otpResult?.errors?.[0] || 'Failed to resend OTP.')
          return
        }
      } else {
        await resendOTP({
          mobileNumber: fullMobileNumber,
          restaurantId,
          storeId,
        })
      }
      setResendCooldown(30)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to resend OTP.')
    } finally {
      setLoading(false)
    }
  }

  // ─── User Details Submit (phone new user only) ─

  const handleUserDetailsSubmit = async () => {
    if (!userDetails.firstName.trim() || !userDetails.lastName.trim()) {
      setError('Please enter your first and last name')
      return
    }
    if (!userDetails.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const signInResult = await signInWithMobileNumber({
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        mobileNumber: fullMobileNumber,
        email: userDetails.email,
        restaurantId,
        storeId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })

      if (signInResult?.errors?.length > 0) {
        setError(signInResult.errors[0] || 'Server error, please try again')
        return
      }

      setStep('otp')
      setResendCooldown(30)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────

  // Show loading screen while processing OAuth redirect
  // Keep showing loading if we're still initializing or if stage is idle (waiting for redirect result)
  if (redirectPending && (authStage === 'idle' || authLoading) && !error) {
    console.log('[LoginPage] Showing redirect loading screen, stage:', authStage, 'loading:', authLoading)
    return (
      <>
        <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-primary px-4">
          <SpinnerIcon className="size-8 text-brand-primary" />
          <p className="mt-4 text-sm/5 text-text-secondary">Completing sign in…</p>
          <p className="mt-2 text-xs text-text-tertiary">
            {authLoading ? 'Processing authentication...' : 'Checking sign-in status...'}
          </p>
        </div>
        
        {/* Debug panel */}
        {import.meta.env.DEV && (
          <div className="fixed bottom-0 left-0 right-0 bg-black/90 text-white text-xs p-2 font-mono overflow-auto max-h-40 z-50">
            <div className="flex gap-4 flex-wrap items-center mb-2">
              <span>Step: {debugInfo.step}</span>
              <span>Stage: {debugInfo.authStage}</span>
              <span>AuthLoading: {debugInfo.authLoading ? 'Y' : 'N'}</span>
              <span>Loading: {debugInfo.loading ? 'Y' : 'N'}</span>
              <span>RedirPending: {debugInfo.redirectPending ? 'Y' : 'N'}</span>
              <span>OAuthNew: {debugInfo.isOAuthNewUser ? 'Y' : 'N'}</span>
              <span>NewUser: {debugInfo.isNewUser ? 'Y' : 'N'}</span>
              <span>AppUser: {debugInfo.hasAppUser ? 'Y' : 'N'}</span>
              <span>Token: {debugInfo.hasIdToken ? 'Y' : 'N'}</span>
              {debugInfo.error && <span className="text-red-400">Error: {debugInfo.error}</span>}
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem('oauth_redirect_pending')
                window.location.reload()
              }}
              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
            >
              Clear & Reload
            </button>
          </div>
        )}
      </>
    )
  }

  // If redirect pending but we have an error, clear the flag and show the error
  if (redirectPending && error) {
    console.log('[LoginPage] Redirect pending but error occurred:', error)
    sessionStorage.removeItem('oauth_redirect_pending')
  }

  return (
    <div
      ref={containerRef}
      className="min-h-dvh flex flex-col bg-surface-primary"
    >
      {/* Header */}
      <header className="sticky top-0 z-20 bg-surface-primary/95 backdrop-blur-xs border-b border-border-light">
        <div className="relative flex items-center justify-center h-14 px-4">
          <button
            onClick={handleBack}
            className="absolute left-4 size-10 flex items-center justify-center rounded-xl text-text-primary hover:bg-surface-tertiary transition-colors"
            aria-label="Go back"
          >
            <ChevronLeftIcon className="size-5" />
          </button>
          <h1 className="font-semibold text-text-primary">
            {step === 'method' && 'Sign In'}
            {step === 'phone-input' && 'Phone Number'}
            {step === 'otp' && 'Verify Code'}
            {step === 'user-details' && 'Your Details'}
          </h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-4 py-6 lg:py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-sm/5 text-red-600 dark:text-red-400 text-center">{error}</p>
            </div>
          )}

          {/* ─── Method Selection ─── */}
          {step === 'method' && (
            <div className="flex flex-col">
              {/* Avatar / branding */}
              <div className="flex flex-col items-center mb-8 lg:mb-10">
                <div className="size-20 lg:size-24 flex items-center justify-center rounded-full bg-surface-tertiary mb-4">
                  <svg className="size-10 lg:size-12 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </div>
                <h2 className="text-xl/7 lg:text-2xl/8 font-bold text-text-primary mb-1">Welcome back</h2>
                <p className="text-sm/5 lg:text-base/6 text-text-secondary text-center">
                  Sign in to access your orders and saved addresses
                </p>
              </div>

              {/* Social sign-in buttons */}
              <div className="flex flex-col gap-3">
                {isOAuthAvailable && (
                  <>
                    <button
                      onClick={handleGoogleLogin}
                      disabled={isBusy}
                      className="
                        w-full h-13 px-4
                        flex items-center justify-center gap-3
                        bg-white dark:bg-surface-elevated
                        border border-border-default
                        rounded-xl shadow-xs
                        font-medium text-text-primary
                        hover:shadow-sm hover:border-border-strong
                        active:scale-[0.98]
                        transition-all duration-200
                        focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary
                        disabled:opacity-50 disabled:pointer-events-none
                      "
                    >
                      {authLoading ? (
                        <SpinnerIcon className="size-5" />
                      ) : (
                        <GoogleIcon className="size-5" />
                      )}
                      Continue with Google
                    </button>

                    <button
                      onClick={handleAppleLogin}
                      disabled={isBusy}
                      className="
                        w-full h-13 px-4
                        flex items-center justify-center gap-3
                        bg-black text-white
                        rounded-xl shadow-xs
                        font-medium
                        hover:bg-gray-900 hover:shadow-sm
                        active:scale-[0.98]
                        transition-all duration-200
                        focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                        disabled:opacity-50 disabled:pointer-events-none
                      "
                    >
                      {authLoading ? (
                        <SpinnerIcon className="size-5" />
                      ) : (
                        <AppleIcon className="size-5" />
                      )}
                      Continue with Apple
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-3">
                      <div className="flex-1 h-px bg-border-light" />
                      <span className="text-xs/4 text-text-tertiary uppercase tracking-wide">or</span>
                      <div className="flex-1 h-px bg-border-light" />
                    </div>
                  </>
                )}

                <button
                  onClick={() => {
                    setError(null)
                    setStep('phone-input')
                  }}
                  disabled={isBusy}
                  className="
                    w-full h-13 px-4
                    flex items-center justify-center gap-3
                    bg-surface-elevated dark:bg-surface-tertiary
                    border border-border-default
                    rounded-xl shadow-xs
                    font-medium text-text-primary
                    hover:shadow-sm hover:border-border-strong
                    active:scale-[0.98]
                    transition-all duration-200
                    focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary
                    disabled:opacity-50 disabled:pointer-events-none
                  "
                >
                  <PhoneDeviceIcon className="size-5 text-text-secondary" />
                  Continue with Phone
                </button>
              </div>

              {/* Skip */}
              <button
                onClick={() => navigate(returnTo)}
                className="mt-6 w-full py-3 text-sm/5 font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                Continue as guest
              </button>

              {/* Terms */}
              <p className="mt-4 text-xs/4 text-center text-text-tertiary">
                By continuing, you agree to our{' '}
                <a href="#" className="underline hover:text-text-secondary">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="underline hover:text-text-secondary">Privacy Policy</a>
              </p>
            </div>
          )}

          {/* ─── Phone Input ─── */}
          {step === 'phone-input' && (
            <div className="flex flex-col">
              <div className="flex flex-col items-center mb-8">
                <div className="size-16 flex items-center justify-center rounded-full bg-surface-tertiary mb-4">
                  <PhoneDeviceIcon className="size-8 text-text-secondary" />
                </div>
                <h2 className="text-lg/7 font-semibold text-text-primary mb-1">Enter your phone number</h2>
                <p className="text-sm/5 text-text-secondary text-center">
                  We'll send a verification code via SMS
                </p>
              </div>

              <div className="flex gap-2 mb-4">
                <select
                  value={phone.countryCode}
                  onChange={(e) => setPhone((p) => ({ ...p, countryCode: e.target.value }))}
                  className="
                    h-13 px-3 w-24
                    bg-surface-elevated dark:bg-surface-tertiary
                    border border-border-default
                    rounded-xl
                    text-text-primary text-sm font-medium
                    focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-transparent
                    appearance-none
                  "
                >
                  <option value="+61">+61</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+91">+91</option>
                  <option value="+64">+64</option>
                  <option value="+65">+65</option>
                </select>

                <input
                  ref={phoneInputRef}
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  value={phone.mobileNumber}
                  onChange={(e) => setPhone((p) => ({ ...p, mobileNumber: e.target.value.replace(/\D/g, '') }))}
                  onKeyDown={(e) => e.key === 'Enter' && handlePhoneSubmit()}
                  placeholder="Mobile number"
                  className="
                    flex-1 h-13 px-4
                    bg-surface-elevated dark:bg-surface-tertiary
                    border border-border-default
                    rounded-xl
                    text-text-primary text-base/6
                    placeholder:text-text-tertiary
                    focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-transparent
                  "
                />
              </div>

              <button
                onClick={handlePhoneSubmit}
                disabled={isBusy || phone.mobileNumber.length < 8}
                className="
                  w-full h-13 mt-2
                  bg-brand-primary text-white
                  rounded-xl font-semibold
                  hover:opacity-90
                  active:scale-[0.98]
                  transition-all duration-200
                  focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  disabled:opacity-40 disabled:pointer-events-none
                  flex items-center justify-center gap-2
                "
              >
                {loading ? (
                  <>
                    <SpinnerIcon className="size-5" />
                    Sending code...
                  </>
                ) : (
                  'Send verification code'
                )}
              </button>
            </div>
          )}

          {/* ─── OTP Verification ─── */}
          {step === 'otp' && (
            <div className="flex flex-col">
              <div className="flex flex-col items-center mb-8">
                <div className="size-16 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                  <svg className="size-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <h2 className="text-lg/7 font-semibold text-text-primary mb-1">Verify your number</h2>
                <p className="text-sm/5 text-text-secondary text-center">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-text-primary">{fullMobileNumber}</span>
                </p>
              </div>

              {/* OTP boxes */}
              <div className="flex justify-center gap-2 lg:gap-3 mb-6">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    autoFocus={i === 0}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={handleOtpPaste}
                    className={`
                      size-12 lg:size-14
                      text-center text-xl/7 font-bold
                      border-2 rounded-xl
                      bg-surface-elevated dark:bg-surface-tertiary
                      text-text-primary
                      transition-all duration-150
                      focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-brand-primary
                      ${digit ? 'border-brand-primary/50' : 'border-border-default'}
                    `}
                  />
                ))}
              </div>

              <button
                onClick={handleOtpSubmit}
                disabled={isBusy || otp.some((d) => !d)}
                className="
                  w-full h-13
                  bg-brand-primary text-white
                  rounded-xl font-semibold
                  hover:opacity-90
                  active:scale-[0.98]
                  transition-all duration-200
                  focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  disabled:opacity-40 disabled:pointer-events-none
                  flex items-center justify-center gap-2
                "
              >
                {loading ? (
                  <>
                    <SpinnerIcon className="size-5" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>

              {/* Resend */}
              <div className="mt-4 text-center">
                {resendCooldown > 0 ? (
                  <p className="text-sm/5 text-text-tertiary">
                    Resend code in <span className="font-medium text-text-primary">{resendCooldown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    disabled={isBusy}
                    className="text-sm/5 font-medium text-brand-primary hover:underline disabled:opacity-50"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ─── User Details (New User) ─── */}
          {step === 'user-details' && (
            <div className="flex flex-col">
              <div className="flex flex-col items-center mb-8">
                <div className="size-16 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <svg className="size-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </div>
                <h2 className="text-lg/7 font-semibold text-text-primary mb-1">Almost there!</h2>
                <p className="text-sm/5 text-text-secondary text-center">
                  Tell us a bit about yourself to complete sign up
                </p>
              </div>

              <div className="flex flex-col gap-3 mb-4">
                <div className="flex gap-3">
                  <input
                    type="text"
                    autoFocus
                    value={userDetails.firstName}
                    onChange={(e) => setUserDetails((d) => ({ ...d, firstName: e.target.value }))}
                    placeholder="First name"
                    className="
                      flex-1 h-13 px-4
                      bg-surface-elevated dark:bg-surface-tertiary
                      border border-border-default
                      rounded-xl
                      text-text-primary text-base/6
                      placeholder:text-text-tertiary
                      focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-transparent
                    "
                  />
                  <input
                    type="text"
                    value={userDetails.lastName}
                    onChange={(e) => setUserDetails((d) => ({ ...d, lastName: e.target.value }))}
                    placeholder="Last name"
                    className="
                      flex-1 h-13 px-4
                      bg-surface-elevated dark:bg-surface-tertiary
                      border border-border-default
                      rounded-xl
                      text-text-primary text-base/6
                      placeholder:text-text-tertiary
                      focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-transparent
                    "
                  />
                </div>
                <input
                  type="email"
                  inputMode="email"
                  value={userDetails.email}
                  onChange={(e) => setUserDetails((d) => ({ ...d, email: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleUserDetailsSubmit()}
                  placeholder="Email address"
                  className="
                    w-full h-13 px-4
                    bg-surface-elevated dark:bg-surface-tertiary
                    border border-border-default
                    rounded-xl
                    text-text-primary text-base/6
                    placeholder:text-text-tertiary
                    focus:outline-hidden focus:ring-2 focus:ring-brand-primary focus:border-transparent
                  "
                />
              </div>

              <button
                onClick={handleUserDetailsSubmit}
                disabled={isBusy || !userDetails.firstName.trim() || !userDetails.lastName.trim() || !userDetails.email.trim()}
                className="
                  w-full h-13 mt-2
                  bg-brand-primary text-white
                  rounded-xl font-semibold
                  hover:opacity-90
                  active:scale-[0.98]
                  transition-all duration-200
                  focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  disabled:opacity-40 disabled:pointer-events-none
                  flex items-center justify-center gap-2
                "
              >
                {loading ? (
                  <>
                    <SpinnerIcon className="size-5" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Safe area bottom padding for iOS */}
      <div className="pb-[env(safe-area-inset-bottom)]" />

      {/* Debug panel - only in development */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/90 text-white text-xs p-2 font-mono overflow-auto max-h-32 z-50">
          <div className="flex gap-4 flex-wrap">
            <span>Step: {debugInfo.step}</span>
            <span>Stage: {debugInfo.authStage}</span>
            <span>AuthLoading: {debugInfo.authLoading ? 'Y' : 'N'}</span>
            <span>Loading: {debugInfo.loading ? 'Y' : 'N'}</span>
            <span>RedirPending: {debugInfo.redirectPending ? 'Y' : 'N'}</span>
            <span>OAuthNew: {debugInfo.isOAuthNewUser ? 'Y' : 'N'}</span>
            <span>NewUser: {debugInfo.isNewUser ? 'Y' : 'N'}</span>
            <span>AppUser: {debugInfo.hasAppUser ? 'Y' : 'N'}</span>
            <span>Token: {debugInfo.hasIdToken ? 'Y' : 'N'}</span>
            {debugInfo.error && <span className="text-red-400">Error: {debugInfo.error}</span>}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Local Icon Components ───────────────────────────────────────

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}

function PhoneDeviceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  )
}
